import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';

const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ContactSupportPage = lazy(() => import('./pages/ContactSupportPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PlatformPage = lazy(() => import('./pages/PlatformPage'));

/**
 * Where to send an authenticated user — super-admins land in the platform
 * console, everyone else on the tenant dashboard. Reads from localStorage
 * directly so it's usable inside route components that don't have useAuth in
 * scope.
 */
const getPostLoginRoute = (): string => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '/dashboard';
    const user = JSON.parse(raw);
    return user?.role === 'super_admin' ? '/platform' : '/dashboard';
  } catch {
    return '/dashboard';
  }
};

// Minimal inline loader — must NOT be lazy-loaded itself
const PageLoader: React.FC = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    width: '100%',
  }}>
    <div style={{
      width: 32,
      height: 32,
      border: '3px solid var(--border, #e2e8f0)',
      borderTopColor: 'var(--accent, #6366f1)',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
  </div>
);

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Public Route Component (redirects authenticated users to their landing page)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('accessToken');
  return !token ? <>{children}</> : <Navigate to={getPostLoginRoute()} replace />;
};

// Tenant-only route — super-admins are redirected away from /dashboard to /platform.
const TenantOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('accessToken');
  if (!token) return <Navigate to="/?auth=login" replace />;
  if (getPostLoginRoute() === '/platform') return <Navigate to="/platform" replace />;
  return <>{children}</>;
};

// Platform-only route — non-super-admins are redirected to /dashboard.
const PlatformOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('accessToken');
  if (!token) return <Navigate to="/?auth=login" replace />;
  if (getPostLoginRoute() !== '/platform') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><Navigate to="/?auth=login" replace /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><Navigate to="/?auth=signup" replace /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><Navigate to="/?auth=forgot-password" replace /></PublicRoute>} />
            <Route path="/reset-password" element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/contact" element={<ContactSupportPage />} />
            <Route path="/dashboard/*" element={
              <TenantOnlyRoute>
                <DashboardPage />
              </TenantOnlyRoute>
            } />
            <Route path="/platform/*" element={
              <PlatformOnlyRoute>
                <PlatformPage />
              </PlatformOnlyRoute>
            } />
          </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
